import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { Users, Phone, Home, CheckCircle } from 'lucide-react';
import StateSlideCarousel from './StateSlideCarousel';

const CLIENT_STATE_ROWS = [
  {
    key: 'affiliated',
    icon: Users,
    dotClass: 'bg-orange-500',
    iconClass: 'text-orange-500',
    chipClass: 'bg-orange-50 text-orange-500 ring-orange-200/70',
    valueClass: 'text-orange-600',
    barClass: 'from-orange-400 to-orange-600',
  },
  {
    key: 'contacted',
    icon: Phone,
    dotClass: 'bg-red-500',
    iconClass: 'text-red-500',
    chipClass: 'bg-red-50 text-red-500 ring-red-200/70',
    valueClass: 'text-red-600',
    barClass: 'from-red-400 to-red-600',
  },
  {
    key: 'visited',
    icon: Home,
    dotClass: 'bg-orange-500',
    iconClass: 'text-orange-500',
    chipClass: 'bg-amber-50 text-amber-500 ring-amber-200/70',
    valueClass: 'text-orange-600',
    barClass: 'from-orange-400 to-orange-600',
  },
  {
    key: 'completed',
    icon: CheckCircle,
    dotClass: 'bg-emerald-500',
    iconClass: 'text-success-500',
    chipClass: 'bg-emerald-50 text-emerald-500 ring-emerald-200/70',
    valueClass: 'text-success-600',
    barClass: 'from-success-400 to-success-600',
  },
];

function ClientStateRow({ row, count, percentage, label, centered = false }) {
  const Icon = row.icon;
  return (
    <div className={centered ? 'mx-auto w-full max-w-sm text-center' : undefined}>
      <div className={`mb-2 flex items-center ${centered ? 'justify-center gap-2' : 'justify-between'}`}>
        <div className="flex items-center gap-2.5">
          {centered ? (
            <span className={`h-2.5 w-2.5 rounded-full ${row.dotClass}`} />
          ) : (
            <span className={`grid h-7 w-7 place-items-center rounded-lg ring-1 ring-inset ${row.chipClass}`}>
              <Icon className="h-4 w-4" />
            </span>
          )}
          <span className="text-sm font-medium text-secondary-700 ">
            {label}
          </span>
        </div>
        {!centered ? (
          <div className="flex items-baseline gap-2">
            <span className={`text-lg font-bold tabular-nums ${row.valueClass}`}>{count}</span>
            <span className="rounded-md bg-secondary-100/80 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-secondary-500  ">
              {percentage}%
            </span>
          </div>
        ) : null}
      </div>
      {centered ? (
        <p className="mb-2 text-lg font-bold tabular-nums text-secondary-900 ">
          <span className={row.valueClass}>{count}</span>
          <span className="ml-2 text-sm font-normal text-secondary-500 ">
            ({percentage}%)
          </span>
        </p>
      ) : null}
      <div className="h-2 w-full overflow-hidden rounded-full bg-secondary-100/80 shadow-[inset_0_1px_2px_rgba(15,23,42,0.06)]">
        <div
          className={`h-2 rounded-full bg-gradient-to-r ${row.barClass} shadow-[0_1px_6px_rgba(249,115,22,0.25)] transition-[width] duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

function ClientStateDistributionCard({
  title,
  stateData = {},
  labels = {},
  footer,
  delay = 0,
  carousel = false,
}) {
  const total = useMemo(
    () => CLIENT_STATE_ROWS.reduce((sum, row) => sum + (stateData[row.key] || 0), 0),
    [stateData]
  );

  const percentages = useMemo(() => {
    if (total <= 0) {
      return { affiliated: 0, contacted: 0, visited: 0, completed: 0 };
    }
    return CLIENT_STATE_ROWS.reduce((acc, row) => {
      acc[row.key] = (((stateData[row.key] || 0) / total) * 100).toFixed(1);
      return acc;
    }, {});
  }, [stateData, total]);

  const carouselItems = useMemo(
    () => CLIENT_STATE_ROWS.map((row) => ({
      key: row.key,
      row,
      count: stateData[row.key] || 0,
      percentage: percentages[row.key],
      label: labels[row.key] || row.key,
    })),
    [stateData, percentages, labels]
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className={carousel ? 'card-premium flex min-h-[6.5rem] items-center justify-center p-4' : 'card-premium p-6'}
    >
      {!carousel && title ? (
        <h3 className="mb-6 flex items-center text-lg font-semibold text-secondary-900 ">
          <ChartBarIcon className="mr-2 h-5 w-5 text-orange-500" />
          {title}
        </h3>
      ) : null}

      {carousel ? (
        <StateSlideCarousel
          className="w-full"
          items={carouselItems}
          renderItem={(item) => (
            <ClientStateRow
              row={item.row}
              count={item.count}
              percentage={item.percentage}
              label={item.label}
              centered
            />
          )}
        />
      ) : (
        <div className="space-y-4">
          {carouselItems.map((item) => (
            <ClientStateRow
              key={item.key}
              row={item.row}
              count={item.count}
              percentage={item.percentage}
              label={item.label}
            />
          ))}
        </div>
      )}

      {footer ? <div className="mt-6 border-t border-secondary-200 pt-4 ">{footer}</div> : null}
    </motion.div>
  );
}

export default ClientStateDistributionCard;
